import { functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';
import { logger } from '@/utils/logger';

export type ServiceError = {
  code: string;
  message: string;
  details?: unknown;
  retry?: boolean;
};

export abstract class BaseService {
  protected async callFunction<TData = unknown, TResponse = unknown>(
    name: string,
    data?: TData,
    options: {
      retry?: boolean;
      timeout?: number;
    } = {}
  ): Promise<TResponse> {
    try {
      const functionCall = httpsCallable<TData, TResponse>(functions, name);
      const result = await functionCall(data);
      return result.data;
    } catch (error) {
      this.handleError(error, name);
    }
  }

  protected handleError(error: unknown, context: string): never {
    // Convert to ServiceError format
    const serviceError: ServiceError = {
      code: 'unknown_error',
      message: 'An unknown error occurred',
      retry: false
    };

    if (error instanceof Error) {
      serviceError.message = error.message;
      if ('code' in error) {
        serviceError.code = (error as { code: string }).code;
      }
    }

    // Log the error with context
    logger.error('BaseService', `Error in ${context}`, { error: serviceError });

    // Add retry suggestion for specific error types
    if (
      serviceError.code === 'functions/deadline-exceeded' ||
      serviceError.code === 'functions/unavailable' ||
      serviceError.code === 'functions/internal'
    ) {
      serviceError.retry = true;
    }

    throw serviceError;
  }

  protected validateResponse<T>(response: unknown, context: string): T {
    if (!response) {
      throw this.handleError(new Error('Empty response received'), context);
    }
    return response as T;
  }
} 